import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Emoji } from './Emoji';
import { formatSeconds, timerState } from './timer-math';
import { cn, type Side } from './util';

export interface TimerBarProps {
  /** Epoch ms (in the same clock as `now()`) when the timer hits zero. */
  deadline: number;
  /** Full duration in ms (bar is 100% at `deadline - durationMs`). */
  durationMs: number;
  /** Clock source, e.g. the store's server-synced `serverNow`. Defaults to `Date.now`. */
  now?: () => number;
  /** Called once per second during the last 3 seconds with 3, 2, 1 — play the `tick` sound here. */
  onTick3s?: (secondsLeft: number) => void;
  /** Called once when the timer reaches zero. */
  onExpire?: () => void;
  /** Bar colour: team colour, jury green or neutral ink. */
  tone?: Side | 'ink';
  /** Where the "⏱ 09" label goes. */
  labelPosition?: 'right' | 'below' | 'none';
  size?: 'sm' | 'md' | 'lg';
  /** Freeze the bar (e.g. while a sale is being revealed). */
  paused?: boolean;
  className?: string;
}

const FILL: Record<NonNullable<TimerBarProps['tone']>, string> = {
  A: 'bg-team-a',
  B: 'bg-team-b',
  J: 'bg-jury',
  ink: 'bg-ink',
};

const HEIGHT = { sm: 'h-3', md: 'h-[18px]', lg: 'h-6' } as const;

/**
 * Shrinking countdown bar driven by requestAnimationFrame (no re-render per frame: the fill is a
 * `scaleX` transform written straight to the DOM; React only re-renders when the seconds label or
 * urgency changes). In the last 3 s it turns red, pulses and the label shakes.
 */
export function TimerBar({
  deadline,
  durationMs,
  now = Date.now,
  onTick3s,
  onExpire,
  tone = 'A',
  labelPosition = 'right',
  size = 'md',
  paused,
  className,
}: TimerBarProps) {
  const fillRef = useRef<HTMLDivElement>(null);
  const cb = useRef({ now, onTick3s, onExpire });
  useEffect(() => {
    cb.current = { now, onTick3s, onExpire };
  });
  const [view, setView] = useState(() => {
    const s = timerState(deadline, durationMs, now());
    return { seconds: s.seconds, urgent: s.urgent, expired: s.expired, fraction: s.fraction };
  });

  useEffect(() => {
    let raf = 0;
    let lastSeconds = -1;
    let expiredFired = false;
    const frame = () => {
      const s = timerState(deadline, durationMs, cb.current.now());
      if (fillRef.current) fillRef.current.style.transform = `scaleX(${s.fraction})`;
      if (s.seconds !== lastSeconds) {
        const first = lastSeconds === -1;
        lastSeconds = s.seconds;
        setView({ seconds: s.seconds, urgent: s.urgent, expired: s.expired, fraction: s.fraction });
        if (!first && s.urgent && s.seconds >= 1 && s.seconds <= 3)
          cb.current.onTick3s?.(s.seconds);
      }
      if (s.expired) {
        if (!expiredFired) {
          expiredFired = true;
          cb.current.onExpire?.();
        }
        return;
      }
      if (!paused) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [deadline, durationMs, paused]);

  const { seconds, urgent, expired } = view;
  const label = labelPosition !== 'none' && (
    <motion.span
      key={urgent ? seconds : 'calm'}
      initial={urgent ? { scale: 1.6, rotate: -8 } : false}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 600, damping: 14 }}
      aria-hidden
      className={cn(
        'comic inline-flex shrink-0 items-center gap-1 tabular-nums',
        size === 'lg' ? 'text-[30px]' : size === 'md' ? 'text-[22px]' : 'text-[17px]',
        urgent || expired ? 'text-team-b ink-text' : 'text-ink',
      )}
    >
      <Emoji emoji="⏱️" label="" size="0.85em" />
      {formatSeconds(seconds)}
    </motion.span>
  );

  return (
    <div
      role="timer"
      aria-label={`Kalan süre ${seconds} saniye`}
      className={cn(
        'flex w-full gap-2.5',
        labelPosition === 'below' ? 'flex-col items-center' : 'items-center',
        className,
      )}
    >
      <div
        className={cn(
          'relative w-full min-w-0 flex-1 overflow-hidden border-3 border-ink bg-white shadow-ink-sm',
          HEIGHT[size],
          urgent && 'animate-pulse-red',
        )}
      >
        <div
          ref={fillRef}
          className={cn(
            'absolute inset-0 origin-left will-change-transform',
            'bg-[repeating-linear-gradient(-45deg,rgb(255_255_255/0.18)_0_6px,transparent_6px_12px)]',
            urgent || expired ? 'bg-team-b' : FILL[tone],
          )}
          style={{ transform: `scaleX(${view.fraction})` }}
        />
        {/* quarter tick marks */}
        <div aria-hidden className="pointer-events-none absolute inset-0 flex justify-evenly">
          {[0, 1, 2].map((i) => (
            <span key={i} className="h-full w-[2px] bg-ink/25" />
          ))}
        </div>
      </div>
      {label}
    </div>
  );
}
